import React, { useState } from "react";
import styled, { css, keyframes } from "styled-components";
import {
  C,
  SEMI,
  REG,
  SegLabel,
  Section,
  TabActiveBar,
  TabLabel,
  TabSub,
  TabButton,
  TabStrip,
  FieldBlock,
  FloatLabel,
  StyledInput,
  RequiredStar,
  ErrorLine,
} from "../Styles";
import { Field } from "formik";
import { states } from "../../../../helpers/constants";
import CustomSelect from "../../../common/CustomSelect/CustomSelect";

export const VERIFY_OPTS = [
  {
    id: "register",
    label: "Register",
    desc: "First time? Create your account.",
  },
  {
    id: "signIn",
    label: "Sign In",
    desc: "Welcome back — sign in to continue.",
  },
];

const CONTACT_FIELDS = [
  {
    name: "name",
    label: "Full Name",
    type: "text",
    placeholder: "Your full name",
    required: true,
  },
  {
    name: "email",
    label: "Email Address",
    type: "email",
    placeholder: "you@example.com",
    required: true,
  },
  {
    name: "phone",
    label: "Mobile Number",
    type: "tel",
    placeholder: "+91 98765 43210",
    required: true,
  },
  {
    name: "houseNumber",
    label: "Address",
    type: "text",
    placeholder: "your address",
    required: true,
  },
  {
    name: "locality",
    label: "Locality",
    type: "tel",
    placeholder: "Your locality",
    required: true,
  },
  {
    name: "city",
    label: "City",
    type: "text",
    placeholder: "Your city",
    required: true,
  },
  {
    name: "state",
    label: "State",
    type: "select",
    required: true,
    menuItems: states,
  },
  /*
  {
    name: "password",
    label: "Password",
    type: "password",
    placeholder: "JustAct@123",
    required: true,
  },
  */
];

export const SUPPORT_SERVICES = [
  {
    key: "mobile",
    label: "Mobile SMS",
    sub: "OTP sent to your phone number",
  },
  {
    key: "email",
    label: "Email",
    sub: "OTP sent to your email address",
  },
];

/* ── Eye SVG icons ── */
const EyeIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
    <path
      d="M1 12C1 12 5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
    <path
      d="M17.94 17.94A10.07 10.07 0 0 1 12 20C5 20 1 12 1 12a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19M1 1l22 22"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/* ──────────────────────────────────────────────────────────────────────────
   SmartField
   • Blocks browser autofill/saved-password dropdown
   • Shows eye toggle on password fields
   • IMPORTANT: does NOT override field.name — Formik tracks state by name,
     overriding it causes the typed value to be written to the wrong key.
────────────────────────────────────────────────────────────────────────── */
const SmartField = ({ field, meta, type, placeholder, id }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [activated, setActivated] = useState(false);

  const isPassword = type === "password";
  const isEmail = type === "email";

  /*
   * Type-swap trick:
   * Before the user first focuses, render email/password fields as type="text"
   * so the browser cannot identify them as credential fields → no dropdown.
   * After focus, switch to the real type so validation & masking work normally.
   */
  let finalType = type;
  if (isPassword) {
    finalType = activated ? (showPassword ? "text" : "password") : "text";
  } else if (isEmail) {
    finalType = activated ? "email" : "text";
  }

  return (
    <PasswordWrapper>
      {/*
       * Honeypot inputs: browsers scan the form for the first
       * text + password pair to pre-fill. These invisible inputs absorb
       * that autofill, leaving the real inputs untouched.
       */}
      <HoneypotInput type="text" tabIndex={-1} aria-hidden="true" />
      <HoneypotInput type="password" tabIndex={-1} aria-hidden="true" />

      <StyledInput
        {...field}
        /* ↑ spreads field.name, field.value, field.onChange, field.onBlur
           — do NOT override field.name here or Formik loses the value */
        id={id}
        type={finalType}
        placeholder={placeholder}
        $err={meta.touched && !!meta.error}
        $hasIcon={isPassword}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        data-lpignore="true" /* LastPass  */
        data-1p-ignore="true" /* 1Password */
        data-bwignore="true" /* Bitwarden */
        data-form-type="other" /* Dashlane  */
        onFocus={() => setActivated(true)}
      />

      {isPassword && (
        <EyeButton
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()} /* keep input focused */
          onClick={() => setShowPassword((v) => !v)}
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          {showPassword ? <EyeOffIcon /> : <EyeIcon />}
        </EyeButton>
      )}
    </PasswordWrapper>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */

const StepVerify = ({ values, setFieldValue }) => {
  return (
    <>
      {/* ── Register / Sign In toggle ── */}
      <Section $delay="0s">
        <SegLabel>How would you like to continue?</SegLabel>
        <TabStrip>
          {VERIFY_OPTS.map((m) => {
            const active = values.typeOfSelect === m.id;
            return (
              <TabButton
                key={m.id}
                type="button"
                $active={active}
                onClick={() => setFieldValue("typeOfSelect", m.id)}
              >
                <TabLabel $active={active}>{m.label}</TabLabel>
                <TabSub $active={active}>{m.desc}</TabSub>
                <TabActiveBar $active={active} />
              </TabButton>
            );
          })}
        </TabStrip>
      </Section>

      {/* ── Form fields ── */}
      {values.typeOfSelect === "register" ? (
        <Section $delay="0.05s">
          <SegLabel>Your Details</SegLabel>
          <FormGrid>
            {CONTACT_FIELDS.map(
              ({ name, label, type, placeholder, required, menuItems }) => (
                <Field key={name} name={name}>
                  {({ field, meta }) => (
                    <FieldBlock>
                      <FloatLabel htmlFor={name}>
                        {label}
                        {required && <RequiredStar>*</RequiredStar>}
                      </FloatLabel>
                      {type === "select" ? (
                        <CustomSelect
                          name={field.name}
                          value={field.value || ""}
                          onChange={(e) =>
                            setFieldValue(field.name, e.target.value)
                          }
                          menuItemValues={menuItems}
                        />
                      ) : (
                        <SmartField
                          field={field}
                          meta={meta}
                          type={type}
                          placeholder={placeholder}
                          id={name}
                          /* no `name` prop — field.name is used directly */
                        />
                      )}

                      {meta.touched && meta.error && (
                        <ErrorLine>{meta.error}</ErrorLine>
                      )}
                    </FieldBlock>
                  )}
                </Field>
              ),
            )}
          </FormGrid>
        </Section>
      ) : (
        <Section $delay="0.05s">
          <SegLabel>Registered Mobile Number</SegLabel>
          <FormGrid style={{ gridTemplateColumns: "1fr" }}>
            <Field name="phone">
              {({ field, meta }) => (
                <FieldBlock>
                  <FloatLabel htmlFor="phone">
                    Mobile Number <RequiredStar>*</RequiredStar>
                  </FloatLabel>
                  <SmartField
                    field={field}
                    meta={meta}
                    type="tel"
                    placeholder="+91 98765 43210"
                    id="phone"
                  />
                  {meta.touched && meta.error && (
                    <ErrorLine>{meta.error}</ErrorLine>
                  )}
                </FieldBlock>
              )}
            </Field>
          </FormGrid>
        </Section>
      )}

      {/* ── OTP delivery method ── */}
      {/* <Section $delay="0.08s">
        <SegLabel>
          {values?.typeOfSelect === "register"
            ? "Send OTP Via"
            : "Sign in with"}
        </SegLabel>
        
        <OtpMethodGrid>
          {SUPPORT_SERVICES.map(({ key, label, sub }) => {
            const active = values.otpService === key;
            return (
              <OtpMethodCard
                key={key}
                type="button"
                $active={active}
                onClick={() => setFieldValue("otpService", key)}
              >
                <OtpMethodIcon $active={active}>
                  {key === "mobile" ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <rect
                        x="5"
                        y="2"
                        width="14"
                        height="20"
                        rx="3"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <circle cx="12" cy="18" r="1" fill="currentColor" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <rect
                        x="3"
                        y="5"
                        width="18"
                        height="14"
                        rx="2"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M3 7l9 6 9-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </OtpMethodIcon>
                <OtpMethodContent>
                  <OtpMethodName $active={active}>{label}</OtpMethodName>
                  <OtpMethodSub $active={active}>{sub}</OtpMethodSub>
                </OtpMethodContent>
                <Toggle $on={active} />
              </OtpMethodCard>
            );
          })}
        </OtpMethodGrid>
      </Section> */}
    </>
  );
};

export default StepVerify;

/* ─── Styled components ──────────────────────────────────────────────────── */

const staggerFade = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

/* Invisible honeypot inputs — attract browser autofill away from real fields */
const HoneypotInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
  top: 0;
  left: 0;
  z-index: -1;
`;

const PasswordWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const EyeButton = styled.button`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${C.mutedMid};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: color 0.18s;

  &:hover {
    color: ${C.navy};
  }
`;

/*

const OtpMethodGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const OtpMethodCard = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 14px;
  border: 1.5px solid ${(p) => (p.$active ? C.teal : C.line)};
  background: ${(p) => (p.$active ? C.tealAlpha : C.surface)};
  cursor: pointer;
  text-align: left;
  transition: all 0.22s ease;
  width: 100%;
  animation: ${staggerFade} 0.35s ease both;

  &:hover {
    border-color: ${C.teal};
    background: ${(p) => (p.$active ? C.tealAlpha : "rgba(0,131,140,0.04)")};
  }
`;

const OtpMethodIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$active ? C.teal : C.white)};
  color: ${(p) => (p.$active ? C.white : C.mutedMid)};
  border: 1.5px solid ${(p) => (p.$active ? C.teal : C.line)};
  flex-shrink: 0;
  transition: all 0.22s;
`;

const OtpMethodContent = styled.div`
  flex: 1;
`;

const OtpMethodName = styled.div`
  font-family: ${SEMI};
  font-weight: 700;
  font-size: 13px;
  color: ${(p) => (p.$active ? C.teal : C.navy)};
  margin-bottom: 3px;
  transition: color 0.2s;
`;

const OtpMethodSub = styled.div`
  font-family: ${REG};
  font-size: 11.5px;
  color: ${(p) => (p.$active ? C.teal : C.muted)};
  line-height: 1.4;
  transition: color 0.2s;
`;

*/

/* ─── Exports used by other steps ────────────────────────────────────────── */

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.button`
  position: relative;
  background: ${(p) => (p.$active ? C.navy : C.white)};
  border: 2px solid ${(p) => (p.$active ? C.teal : C.line)};
  border-radius: 14px;
  padding: 22px 20px 18px;
  cursor: pointer;
  text-align: left;
  transition: all 0.24s ease;
  overflow: hidden;
  width: 100%;
  animation: ${staggerFade} 0.4s ease both;
  animation-delay: ${(p) => p.$i * 0.06}s;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: ${(p) => (p.$active ? C.teal : "transparent")};
    transition: background 0.22s;
  }
  &:hover {
    border-color: ${C.teal};
    transform: translateY(-3px);
    box-shadow: 0 10px 32px rgba(0, 131, 140, 0.16);
    background: ${(p) => (p.$active ? C.navy : C.white)};
  }
  &:active {
    transform: translateY(-1px);
  }
  ${(p) =>
    p.$active &&
    css`
      box-shadow: 0 8px 28px rgba(41, 52, 97, 0.22);
    `}
`;

export const RoomNumber = styled.div`
  font-family: ${SEMI};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${(p) => (p.$active ? "rgba(255,255,255,0.45)" : C.muted)};
  margin-bottom: 6px;
  transition: color 0.2s;
`;

export const RoomName = styled.div`
  font-family: ${SEMI};
  font-weight: 700;
  font-size: 16px;
  color: ${(p) => (p.$active ? C.white : C.navy)};
  margin-bottom: 6px;
  letter-spacing: -0.015em;
  transition: color 0.2s;
  padding-right: ${(p) => (p.$active ? "28px" : "0")};
`;

export const RoomDesc = styled.div`
  font-family: ${REG};
  font-size: 12.5px;
  color: ${(p) => (p.$active ? "rgba(255,255,255,0.58)" : C.textSub)};
  line-height: 1.55;
  margin-bottom: 16px;
  transition: color 0.2s;
`;

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const MetaPill = styled.span`
  font-family: ${SEMI};
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 99px;
  letter-spacing: 0.02em;
  transition: all 0.2s;
  ${(p) =>
    p.$primary
      ? css`
          background: ${p.$active ? "rgba(0,131,140,0.25)" : C.tealAlpha};
          border: 1px solid ${p.$active ? "rgba(0,131,140,0.35)" : C.tealLine};
          color: ${p.$active ? "rgba(255,255,255,0.85)" : C.teal};
        `
      : css`
          background: ${p.$active ? "rgba(255,255,255,0.08)" : C.surface};
          border: 1px solid ${p.$active ? "rgba(255,255,255,0.14)" : C.line};
          color: ${p.$active ? "rgba(255,255,255,0.55)" : C.mutedMid};
        `}
`;

export const SvcListOne = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  border: 1.5px solid ${C.line};
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 28px;
  background: ${C.white};
`;
